import { FillDirectionIcon, areaCountryList, countryCode2IdMap, idAndNameCountryObj } from '@/constants';
import { phoneAreaCode } from '@/constants/mcn';
import { i18nConfig } from '@/hooks/useLanguage';
import type { Locales } from '@/hooks/useLocal';
import { MobileBottomPopup, MobileFilterActions } from '@/page-components/common/offers/mobileOfferFilters/MobileFilterPrimitives';
import {
  ALL_COUNTRIES,
  formatCountryFlag,
  formatGroupTitle,
  getChineseGroup,
  getCountryGroups,
  getCountryName,
  getJapaneseGroup,
} from '@/page-components/common/offers/mobileOfferFilters/filterUtils';
import type { CountryNameKey } from '@/page-components/common/offers/mobileOfferFilters/filterUtils';
import { CloseCircleFilled } from '@ant-design/icons';
import { Checkbox, Input, Radio } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './MobileCountryPicker.module.scss';

export type MobileCountryPath = [area: string, country: string | number];
export type MobileCountryPickerValue = string | number;

export interface MobileCountryPickerOption {
  value: MobileCountryPickerValue;
  label: string;
  flag: string;
  group: string;
  searchValues: Array<string | undefined>;
  secondaryLabel?: string;
}

export const getMobileCountryPickerOptions = (locale: Locales, showPhoneCode = false): MobileCountryPickerOption[] => {
  const countryNameKey = i18nConfig.areaCountryTwoMap[locale] as CountryNameKey;

  return Object.entries(
    getCountryGroups('', countryNameKey, locale, {
      filterCountry: showPhoneCode ? (country) => Boolean(country.phoneCode) : undefined,
    }),
  ).flatMap(([group, countries]) =>
    countries.map((country) => ({
      value: country.countryId,
      label: getCountryName(country, countryNameKey),
      flag: formatCountryFlag(country.twoLetterCode),
      group,
      searchValues: [country.chineseName, country.englishName, country.jaName, country.twoLetterCode, showPhoneCode ? country.phoneCode : undefined],
      secondaryLabel: showPhoneCode && country.phoneCode ? `(${country.phoneCode})` : undefined,
    })),
  );
};

interface DefaultMobileCountryPickerProps {
  mode?: 'default';
  value?: MobileCountryPath;
  valueType?: 'id' | 'code';
  placeholder: string;
  title?: string;
  searchPlaceholder?: string;
  showPhoneCode?: boolean;
  displayPhoneCode?: boolean;
  allowClear?: boolean;
  onChange?: (value?: MobileCountryPath) => void;
  onBlur?: () => void;
}

interface PhoneCodeMobileCountryPickerProps {
  mode: 'phoneCode';
  value?: number;
  allowDeselect?: boolean;
  placeholder: string;
  title?: string;
  searchPlaceholder?: string;
  onChange?: (value?: number) => void;
  onBlur?: () => void;
}

interface ConfiguredMobileCountryPickerProps {
  mode: 'configured';
  options: MobileCountryPickerOption[];
  visible: boolean;
  title: ReactNode;
  searchPlaceholder: string;
  selectedValues: MobileCountryPickerValue[];
  multiple?: boolean;
  allowDeselect?: boolean;
  featuredOptions?: MobileCountryPickerOption[];
  featuredTitle?: ReactNode;
  showReset?: boolean;
  destroyOnClose?: boolean;
  onClose: () => void;
  onConfirm: (values: MobileCountryPickerValue[]) => void;
  onReset?: () => void;
}

type MobileCountryPickerProps = DefaultMobileCountryPickerProps | PhoneCodeMobileCountryPickerProps | ConfiguredMobileCountryPickerProps;

const normalizeSearchValue = (value: string) => value.trim().toLocaleLowerCase();
const defaultPhoneCodes = Array.from(new Set(phoneAreaCode.map(Number)));

const ConfiguredMobileCountryPicker = ({
  mode: _mode,
  visible,
  title,
  searchPlaceholder,
  options,
  selectedValues,
  multiple = false,
  allowDeselect = true,
  featuredOptions = [],
  featuredTitle,
  showReset = false,
  destroyOnClose,
  onClose,
  onConfirm,
  onReset,
}: ConfiguredMobileCountryPickerProps) => {
  const [keyword, setKeyword] = useState('');
  const [draftValues, setDraftValues] = useState<MobileCountryPickerValue[]>(selectedValues);

  // 打开时清空搜索框 & 初始化 draftValues
  useEffect(() => {
    if (!visible) return;
    setKeyword('');
    setDraftValues(selectedValues);
  }, [selectedValues, visible]);

  // 根据搜索关键词筛选国家选项，再按 group 分组并排序
  const groups = useMemo(() => {
    const normalizedKeyword = normalizeSearchValue(keyword);
    const filteredOptions = normalizedKeyword
      ? options.filter((option) => option.searchValues.some((value) => value && normalizeSearchValue(value).includes(normalizedKeyword)))
      : options;

    return Object.entries(
      filteredOptions.reduce<Record<string, MobileCountryPickerOption[]>>((result, option) => {
        (result[option.group] ||= []).push(option);
        return result;
      }, {}),
    ).sort(([firstGroup], [secondGroup]) => firstGroup.localeCompare(secondGroup));
  }, [keyword, options]);

  const updateValue = (value: MobileCountryPickerValue, checked: boolean) => {
    setDraftValues((previousValues) => {
      if (!multiple) return checked ? [value] : [];
      if (checked) return previousValues.includes(value) ? previousValues : [...previousValues, value];
      return previousValues.filter((previousValue) => previousValue !== value);
    });
  };

  const renderOption = (option: MobileCountryPickerOption) => {
    const selected = draftValues.includes(option.value);
    const countryInfo = (
      <span className={styles.countryInfo}>
        <span className={styles.countryFlag} aria-hidden>
          {option.flag}
        </span>
        <span className={styles.countryName}>{option.label}</span>
        {option.secondaryLabel ? <span className={styles.secondaryLabel}>{option.secondaryLabel}</span> : null}
      </span>
    );

    if (multiple) {
      return (
        <Checkbox
          key={option.value}
          checked={selected}
          className={classNames(styles.countryOption, styles.multipleOption)}
          onChange={(event) => updateValue(option.value, event.target.checked)}
        >
          {countryInfo}
        </Checkbox>
      );
    }

    return (
      <Radio
        key={option.value}
        checked={selected}
        className={classNames(styles.countryOption, styles.singleOption)}
        onClick={() => updateValue(option.value, selected ? !allowDeselect : true)}
      >
        {countryInfo}
      </Radio>
    );
  };

  const renderGroup = (key: string, groupTitle: ReactNode, groupOptions: MobileCountryPickerOption[]) => (
    <section key={key} className={styles.countryGroup}>
      <h3 className={styles.countryGroupTitle}>{groupTitle}</h3>
      <div className={styles.countryGroupItems}>{groupOptions.map(renderOption)}</div>
    </section>
  );

  return (
    <MobileBottomPopup visible={visible} destroyOnClose={destroyOnClose} onClose={onClose}>
      <div className={styles.popup}>
        <div className={styles.content}>
          <h2 className={styles.title}>{title}</h2>
          <Input className={styles.search} value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder={searchPlaceholder} />
          <div className={styles.list} role={multiple ? undefined : 'radiogroup'} aria-label={typeof title === 'string' ? title : undefined}>
            {!keyword && featuredOptions.length && featuredTitle ? renderGroup('featured', featuredTitle, featuredOptions) : null}
            {groups.map(([group, groupOptions]) => renderGroup(group, formatGroupTitle(group), groupOptions))}
            {!(!keyword && featuredOptions.length) && !groups.length ? (
              <div className={styles.empty} role="status">
                <FormattedMessage defaultMessage="No matching results" description="无匹配结果" />
              </div>
            ) : null}
          </div>
        </div>
        <MobileFilterActions
          className={classNames(styles.actions, { [styles.multipleActions]: multiple })}
          showReset={showReset}
          onReset={() => {
            setDraftValues([]);
            onReset?.();
            onClose();
          }}
          onConfirm={() => {
            onConfirm(draftValues);
            onClose();
          }}
        />
      </div>
    </MobileBottomPopup>
  );
};

const PhoneCodeMobileCountryPicker = ({
  mode: _mode,
  value,
  allowDeselect = false,
  placeholder,
  title,
  searchPlaceholder,
  onChange,
  onBlur,
}: PhoneCodeMobileCountryPickerProps) => {
  const { formatMessage, locale } = useIntl();
  const currentLocale = locale as Locales;
  const countryNameKey = i18nConfig.areaCountryTwoMap[currentLocale] as CountryNameKey;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const options = useMemo<MobileCountryPickerOption[]>(
    () =>
      defaultPhoneCodes.map((phoneCode) => {
        const countries = ALL_COUNTRIES.filter((country) => country.phoneCode === `+${phoneCode}`);
        const country = countries[0];

        return {
          value: phoneCode,
          label: country ? getCountryName(country, countryNameKey) : `+${phoneCode}`,
          flag: country ? formatCountryFlag(country.twoLetterCode) : '🌐',
          group:
            currentLocale === 'ja-JP'
              ? getJapaneseGroup(country?.jaName ?? '')
              : currentLocale === 'zh-CN'
              ? getChineseGroup(country?.chineseName ?? '')
              : country?.englishName.trim().charAt(0).toLocaleUpperCase() || '#',
          searchValues: [
            String(phoneCode),
            `+${phoneCode}`,
            ...countries.flatMap(({ chineseName, englishName, jaName, twoLetterCode }) => [chineseName, englishName, jaName, twoLetterCode]),
          ],
          secondaryLabel: country ? `(+${phoneCode})` : undefined,
        };
      }),
    [countryNameKey, currentLocale],
  );

  const handleClose = () => {
    setVisible(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <>
      <div className={styles.trigger}>
        <button ref={triggerRef} type="button" className={styles.triggerButton} onClick={() => setVisible(true)}>
          <span className={value === undefined ? styles.placeholder : styles.value}>{value === undefined ? placeholder : `+${value}`}</span>
          <FillDirectionIcon direction="down" size={20} />
        </button>
      </div>

      <ConfiguredMobileCountryPicker
        mode="configured"
        visible={visible}
        title={
          title ||
          formatMessage({
            defaultMessage: 'Select Country Code',
            description: '选择国家区号',
          })
        }
        searchPlaceholder={
          searchPlaceholder ||
          formatMessage({
            defaultMessage: 'Search country name or code',
            description: '搜索国家名称或区号',
          })
        }
        options={options}
        selectedValues={value === undefined ? [] : [value]}
        allowDeselect={allowDeselect}
        onClose={handleClose}
        onConfirm={(values) => {
          onChange?.(values[0] as number | undefined);
          onBlur?.();
        }}
      />
    </>
  );
};

const DefaultMobileCountryPicker = ({
  value,
  valueType = 'id',
  placeholder,
  title,
  searchPlaceholder,
  showPhoneCode = false,
  displayPhoneCode = false,
  allowClear = true,
  onChange,
  onBlur,
}: DefaultMobileCountryPickerProps) => {
  const { formatMessage, locale } = useIntl();
  const currentLocale = locale as Locales;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);
  const countryValue = value?.[1];
  const selectedCountryId = valueType === 'code' ? countryCode2IdMap[String(countryValue)] : Number(countryValue) || undefined;
  const selectedCountry = selectedCountryId ? idAndNameCountryObj[selectedCountryId] : undefined;
  const selectedLabel = displayPhoneCode ? selectedCountry?.phoneCode : selectedCountry?.[i18nConfig.areaCountryTwoMap[currentLocale]];
  const options = useMemo<MobileCountryPickerOption[]>(
    () => getMobileCountryPickerOptions(currentLocale, showPhoneCode),
    [currentLocale, showPhoneCode],
  );

  const commitCountry = (countryId?: number) => {
    if (!countryId) {
      onChange?.(undefined);
      onBlur?.();
      return;
    }

    const country = idAndNameCountryObj[countryId];
    const area = areaCountryList.find((item) => item.countryList.some((areaCountry) => areaCountry.countryId === countryId));
    if (!country || !area) return;

    onChange?.([area[i18nConfig.areaCountryOneMap[currentLocale]], valueType === 'code' ? country.twoLetterCode : country.countryId]);
    onBlur?.();
  };

  const handleClose = () => {
    setVisible(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <>
      <div className={styles.trigger}>
        <button ref={triggerRef} type="button" className={styles.triggerButton} onClick={() => setVisible(true)}>
          <span className={selectedLabel ? styles.value : styles.placeholder}>{selectedLabel || placeholder}</span>
          {allowClear && selectedCountry ? null : <FillDirectionIcon direction="down" size={20} />}
        </button>
        {allowClear && selectedCountry ? (
          <button type="button" className={styles.clearButton} aria-label={`Clear ${selectedLabel}`} onClick={() => commitCountry(undefined)}>
            <CloseCircleFilled />
          </button>
        ) : null}
      </div>

      <ConfiguredMobileCountryPicker
        mode="configured"
        visible={visible}
        title={
          title ||
          formatMessage({
            defaultMessage: 'Select Country/Region',
            description: '选择国家/地区',
          })
        }
        searchPlaceholder={
          searchPlaceholder ||
          formatMessage({
            defaultMessage: 'Search country name',
            description: '搜索国家名称',
          })
        }
        options={options}
        showReset={allowClear && Boolean(value)}
        selectedValues={selectedCountryId === undefined ? [] : [selectedCountryId]}
        onClose={handleClose}
        onConfirm={(values) => commitCountry(values[0] as number | undefined)}
      />
    </>
  );
};

const MobileCountryPicker = (props: MobileCountryPickerProps) =>
  props.mode === 'configured' ? (
    <ConfiguredMobileCountryPicker {...props} />
  ) : props.mode === 'phoneCode' ? (
    <PhoneCodeMobileCountryPicker {...props} />
  ) : (
    <DefaultMobileCountryPicker {...props} />
  );

export default MobileCountryPicker;
